<script>
  import { createEventDispatcher, onDestroy } from "svelte";
  import { userLevelStore, currentLevelsStore } from "./stores.js";

  import Popout from "./Popout.svelte";

  const dispatch = createEventDispatcher();

  let currentLevels = [];
  let allAvailableLevels = [];

  let maxLevel;

  $: if ($userLevelStore && !maxLevel) {
    maxLevel = parseInt($userLevelStore);
    for (let i = maxLevel; i > 0; i--) {
      allAvailableLevels.push(i);
    }
    allAvailableLevels = allAvailableLevels;
  }

  $: isActiveLevel = level => {
    if (currentLevels.includes(level)) return "active";
  };

  $: toggleActiveLevel = level => {
    if (currentLevels.includes(level)) {
      currentLevels.splice(currentLevels.indexOf(level), 1);
    } else {
      currentLevels.push(level);
    }
    currentLevels = currentLevels.sort((a, b) => a - b);
    return dispatch("levelsChange", {
      levels: currentLevels
    });
  };

  const unsubscribeCurrentLevelStore = currentLevelsStore.subscribe(value => {
    currentLevels = value;
  });

  let inputVal = currentLevels[0];
  const unsubscribeUserStore = userLevelStore.subscribe(value => {
    if (!inputVal) {
      inputVal = value;
    }
  });

  let lastValue = inputVal;
  let isFocused = false;

  onDestroy(() => {
    unsubscribeUserStore();
    unsubscribeCurrentLevelStore();
  });

  const dashRegex = /\d+-\d+/; // ex: "4-6"
  const spaceRegex = /[,?\s+|,]/g; // ex: "1, 2, 3 4 5"

  function processLevelSelection(event) {
    isFocused = false;
    const inputStr = event.target.textContent.trim();
    if (inputStr === lastValue.trim()) return;
    let levelsArray = inputStr.split(spaceRegex);
    let returnArray = [];

    levelsArray.forEach((level, idx) => {
      if (level.match(dashRegex)) {
        const nums = level.split(/\s?-\s?/).map(num => parseInt(num));
        const [start, end] = nums;
        for (let i = start; i <= end; i++) {
          returnArray.push(i);
        }
      } else {
        returnArray.push(parseInt(level));
      }
    });
    let cache = {};
    returnArray = returnArray.filter(num => {
      if (cache[num]) return false;
      cache[num] = true;
      return num > 0 && num <= 60;
    });
    return dispatch("levelsChange", {
      levels: returnArray
    });
  }

  function processKeyDown(event) {
    const ENTER_KEY = 13;
    if (event.which === ENTER_KEY) {
      event.preventDefault();
      event.target.blur();
    }
  }

  function setFocus(event) {
    lastValue = event.target.textContent;
    isFocused = true;
  }
</script>

<style>
  .text-input-area {
    display: flex;
    align-items: center;
    position: relative;
  }

  label {
    margin-right: 0.5rem;
  }

  .editable-input {
    border: none;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 1rem;
    text-align: center;
    margin: 0;
    min-width: 25px;
    padding: 0.2rem;
    white-space: nowrap;
  }

  ul {
    list-style: none;
    padding: 0;
    margin-bottom: 0;
    margin-top: 0.5rem;
  }

  li {
    margin-bottom: 0.25rem;
  }

  li > strong {
    display: inline-block;
    width: 75px;
  }

  .examples {
    text-align: left;
  }

  .button-group {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    grid-template-rows: auto;
    grid-gap: 0.2rem;
    margin-top: 1rem;
  }

  .button-group button {
    cursor: pointer;
    padding: 0.3rem;
    font-size: 12px;
    border-radius: 6px;
    box-shadow: var(--button-tiny-box-shadow);
    border: 1px solid #ddd;
    background: none;
    transition: all 0.3s, color 0;
  }

  .button-group .active {
    background: var(--blue);
    border: 1px solid var(--blue);
    color: white;
  }

  .button-group button:hover {
    transform: translate(0, -1px);
    box-shadow: 0px 2px 3px rgba(112, 40, 45, 0.2);
  }

  @media screen and (max-width: 768px) {
    label {
      font-size: 1.2rem;
    }

    .text-input-area {
      align-items: center;
      flex: initial;
      justify-content: flex-start;
      margin-top: 1rem;
    }

    .button-group {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
      grid-gap: 0.5rem;
    }

    .button-group button {
      font-size: 1rem;
      padding: 0.3rem;
    }
  }
</style>

<section>
  <div class="text-input-area">
    <label for="level-select">Selected Levels</label>
    <span
      class="editable-input"
      contenteditable="true"
      type="text"
      data-testid="level_select"
      on:blur={event => processLevelSelection(event)}
      on:keydown={event => processKeyDown(event)}
      on:focusin={event => setFocus(event)}>
      {inputVal}
    </span>
    <Popout visible={isFocused}>
      <div class="examples">
        <strong>Formatting examples:</strong>
        <ul>
          <li>
            <strong>3</strong>
            Level 3
          </li>
          <li>
            <strong>2,5,6</strong>
            Levels 2, 5 and 6
          </li>
          <li>
            <strong>1-5</strong>
            Levels 1 through 5
          </li>
          <li>
            <strong>3-7 9 12</strong>
            Levels 3 through 7, 9 and 12
          </li>
        </ul>
      </div>
    </Popout>
  </div>
  <div class="button-group">
    {#each allAvailableLevels as level}
      <button
        class={isActiveLevel(level)}
        on:click={() => toggleActiveLevel(level)}>
        {level}
      </button>
    {/each}
  </div>
</section>
